import { useEffect, useState } from "react";
import { toast } from "sonner";
import { House, IdentificationCard, X } from "@phosphor-icons/react";
import { DashboardLayout } from "../components/layout/DashboardLayout";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { useEditConfirm } from "../context/EditConfirmContext";
import { formatET } from "../lib/dateUtils";

const STAGES = ["In Progress", "Intake Complete", "Awaiting Review", "Payment Link Sent", "Paid", "Trust Delivered"];
const STAGE_COLORS = {
  "In Progress": "text-igc-muted", "Intake Complete": "text-igc-goldText",
  "Awaiting Review": "text-blue-700", "Payment Link Sent": "text-igc-goldText",
  "Paid": "text-igc-success", "Trust Delivered": "text-igc-success",
};
const TRUST_PACKAGES = [
  { value: "basic", label: "Basic Living Trust — $1,497" },
  { value: "private", label: "Private Trust — $2,497" },
];
const navItems = [
  { to: "/admin/trust", label: "Trust Pipeline", icon: <House size={18} weight="duotone" />, testId: "nav-admin-trust" },
  { to: "/admin/trust-control-panel", label: "Trust Control Panel", icon: <IdentificationCard size={18} weight="duotone" />, testId: "nav-admin-trust-control-panel" },
];

const SECTION_FIELD_LABELS = {
  personal_info: { full_name: "Full Legal Name", date_of_birth: "Date of Birth", marital_status: "Marital Status", mailing_address: "Mailing Address" },
  trust_structure: { trust_type: "Trust Type", structure_notes: "Structure Notes" },
  beneficiaries: { primary_beneficiary_name: "Primary Beneficiary", primary_beneficiary_relationship: "Relationship", secondary_beneficiary_name: "Secondary Beneficiary", secondary_beneficiary_relationship: "Relationship" },
  asset_protection: { assets_summary: "Assets Summary" },
};

export default function AdminTrustControlPanelPage() {
  const confirmEdit = useEditConfirm();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [docs, setDocs] = useState([]);
  const [notesDraft, setNotesDraft] = useState("");
  const [modal, setModal] = useState(null);
  const [releasingId, setReleasingId] = useState(null);
  const [packageChoice, setPackageChoice] = useState({});

  const fetchClients = async () => {
    try {
      const { data } = await api.get("/admin/trust-portal/clients");
      setClients(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
    const interval = setInterval(fetchClients, 15000);
    return () => clearInterval(interval);
  }, []);

  const openDetail = async (id, view) => {
    const { data } = await api.get(`/admin/trust-portal/clients/${id}`);
    setDetail(data);
    setNotesDraft(data.notes || "");
    if (view === "documents") {
      const { data: docData } = await api.get(`/admin/trust-portal/clients/${id}/documents`);
      setDocs(docData);
    }
    setModal(view);
  };

  const updateStage = async (id, stage) => {
    const c = clients.find((x) => x.id === id);
    if (!(await confirmEdit(`${c?.display_name || "this client"}'s trust stage`))) return;
    try {
      await api.patch(`/admin/trust-portal/clients/${id}`, { trust_stage: stage });
      toast.success("Stage updated");
      fetchClients();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const saveNotes = async () => {
    if (!(await confirmEdit(`${detail?.display_name || "this client"}'s notes`))) return;
    try {
      await api.patch(`/admin/trust-portal/clients/${detail.id}`, { notes: notesDraft });
      toast.success("Notes saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const releasePaymentLink = async (id) => {
    const c = clients.find((x) => x.id === id);
    if (!(await confirmEdit(`${c?.display_name || "this client"}'s payment link (release)`))) return;
    const trust_product = packageChoice[id] || "basic";
    setReleasingId(id);
    try {
      await api.post(`/admin/trust-portal/clients/${id}/release-payment-link`, { trust_product, origin_url: window.location.origin });
      toast.success("Payment link sent to client");
      fetchClients();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setReleasingId(null);
    }
  };

  return (
    <DashboardLayout navItems={navItems} moduleName="Trust & Asset Protection" moduleBackPath="/admin">
      <div data-testid="admin-trust-control-panel-page">
        <h1 className="font-serif text-3xl text-igc-cream tracking-tight mb-2" title="Every trust.igchq.com client account, their intake progress, documents, and payment stage.">Trust Control Panel</h1>
        <p className="text-igc-muted text-sm mb-8">Every trust.igchq.com client account, intake stage, and document — real-time.</p>

        {clients.length === 0 && !loading && <p className="text-igc-muted text-sm" data-testid="trust-control-panel-empty">No Trust Portal clients yet.</p>}

        <div className="space-y-3">
          {clients.map((c) => (
            <div key={c.id} className="bg-igc-surface border border-igc-gold/15 p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`trust-control-client-${c.id}`}>
              <div>
                <p className="text-igc-cream text-sm font-medium">{c.display_name}</p>
                <p className="text-igc-muted text-xs">{c.email} {c.phone && `— ${c.phone}`} — started {formatET(c.date_started, "MMM d, yyyy")}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <Select value={c.trust_stage} onValueChange={(v) => updateStage(c.id, v)}>
                  <SelectTrigger data-testid={`trust-control-stage-select-${c.id}`} title="Where this client's trust setup is at, from intake through delivery." className={`w-52 bg-igc-surfaceLight border-igc-gold/20 ${STAGE_COLORS[c.trust_stage] || "text-igc-cream"}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                    {STAGES.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                  </SelectContent>
                </Select>
                <Button data-testid={`trust-control-view-submission-${c.id}`} onClick={() => openDetail(c.id, "submission")} variant="outline" title="Opens this client's full intake form answers." className="border-igc-gold/30 text-igc-goldText">View Intake</Button>
                <Button data-testid={`trust-control-view-documents-${c.id}`} onClick={() => openDetail(c.id, "documents")} variant="outline" title="Opens the list of documents this client has uploaded." className="border-igc-gold/30 text-igc-goldText">View Documents</Button>
                {c.trust_stage === "Awaiting Review" && (
                  <>
                    <Select value={packageChoice[c.id] || "basic"} onValueChange={(v) => setPackageChoice({ ...packageChoice, [c.id]: v })}>
                      <SelectTrigger data-testid={`trust-control-package-select-${c.id}`} title="Choose which trust package this client is purchasing before sending the payment link." className="w-48 bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-igc-surfaceLight border-igc-gold/20 text-igc-cream">
                        {TRUST_PACKAGES.map((p) => (<SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>))}
                      </SelectContent>
                    </Select>
                    <Button data-testid={`trust-control-release-payment-${c.id}`} disabled={releasingId === c.id} onClick={() => releasePaymentLink(c.id)} title="Emails this client a payment link for the selected trust package." className="bg-igc-gold hover:bg-igc-goldHover text-black">
                      {releasingId === c.id ? "Sending..." : "Release Payment Link"}
                    </Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>

        {modal && detail && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-6" data-testid="trust-control-modal">
            <div className="bg-igc-surface border border-igc-gold/20 max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6 relative">
              <button data-testid="trust-control-modal-close" onClick={() => setModal(null)} title="Closes this window." className="absolute top-4 right-4 text-igc-muted hover:text-igc-cream"><X size={20} /></button>
              <h2 className="font-serif text-xl text-igc-cream tracking-tight mb-4">{detail.display_name}</h2>
              {modal === "submission" && (
                <div className="space-y-4">
                  {Object.entries(detail.intake?.sections || {}).map(([key, sec]) => (
                    <div key={key} data-testid={`trust-control-submission-section-${key}`}>
                      <p className="text-igc-goldText text-sm font-medium mb-1 capitalize">{key.replaceAll("_", " ")} {sec.completed ? "✓" : "(incomplete)"}</p>
                      <div className="bg-igc-surfaceLight p-3 space-y-1">
                        {Object.keys(SECTION_FIELD_LABELS[key] || {}).map((fieldKey) => (
                          <div key={fieldKey} className="flex gap-2 text-xs">
                            <span className="text-igc-muted min-w-[140px]">{SECTION_FIELD_LABELS[key][fieldKey]}:</span>
                            <span className="text-igc-cream">{sec.data?.[fieldKey] || "—"}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="pt-4 border-t border-igc-gold/10">
                    <p className="text-igc-goldText text-sm font-medium mb-1">Internal Notes</p>
                    <Textarea data-testid="trust-control-notes-input" value={notesDraft} onChange={(e) => setNotesDraft(e.target.value)} title="Private notes about this client, visible only to admins." className="bg-igc-surfaceLight border-igc-gold/20 mb-2" />
                    <Button data-testid="trust-control-notes-save" onClick={saveNotes} title="Saves the note you just typed." className="bg-igc-gold hover:bg-igc-goldHover text-black">Save Notes</Button>
                  </div>
                </div>
              )}
              {modal === "documents" && (
                <div className="space-y-2">
                  {docs.length === 0 && <p className="text-igc-muted text-sm" data-testid="trust-control-documents-empty">No documents uploaded.</p>}
                  {docs.map((d) => (
                    <a key={d.id} href={`${API}/trust-portal/documents/${d.id}/download`} target="_blank" rel="noreferrer" data-testid={`trust-control-document-${d.id}`} title="Opens or downloads this document." className="block bg-igc-surfaceLight p-3 text-igc-cream text-sm hover:text-igc-goldText">
                      {d.original_filename} — {formatET(d.uploaded_at, "MMM d, yyyy h:mm a")}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
